/**
 * Inicio. «Buenos días, Admin» con las tres pestañas: Resumen / Rendimiento / Analítica.
 *
 * La pestaña activa vive en la URL (`?vista=`) y no en el estado del componente: así se puede
 * compartir un enlace a la pestaña de Rendimiento y el botón atrás del navegador funciona.
 */
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { SummaryTab } from './dashboard/SummaryTab';
import { PerformanceTab } from './dashboard/PerformanceTab';
import { AnalyticsTab } from './dashboard/AnalyticsTab';
import styles from './DashboardPage.module.css';

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'rendimiento', label: 'Rendimiento' },
  { id: 'analitica', label: 'Analítica' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function isTabId(value: string | null): value is TabId {
  return TABS.some((tab) => tab.id === value);
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('vista');
  const active: TabId = isTabId(requested) ? requested : 'resumen';

  // El saludo y la fecha los da la API para que la cabecera no dependa del reloj del navegador.
  const { data: summary } = useQuery({
    queryKey: queryKeys.dashboardSummary,
    queryFn: api.dashboardSummary,
  });

  return (
    <section>
      <PageHeader
        size="dashboard"
        title={`${summary?.greeting ?? 'Hola'}, Admin`}
        subtitle={
          summary ? `Resumen de tu cartera a ${summary.today}.` : 'Cargando tu cartera…'
        }
      />

      <div className={styles.tabs} role="tablist" aria-label="Vistas del panel">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            className={active === tab.id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setSearchParams(tab.id === 'resumen' ? {} : { vista: tab.id })}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {active === 'resumen' ? <SummaryTab /> : null}
      {active === 'rendimiento' ? <PerformanceTab /> : null}
      {active === 'analitica' ? <AnalyticsTab /> : null}
    </section>
  );
}
